import React, { type ReactNode } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { useAppSelector } from 'hooks/redux';

import { aiChatSelectors } from 'app-components/flux/ai-chat';
import { preferencesV2Selectors, usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { isDemoMode } from 'app-components/utils/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';

import ChatLauncher, { ChatDockProvider, useChatDock } from 'components/ChatLauncher';
import ErrorBoundary from 'components/ErrorBoundary';
import Header from 'containers/App/Header';
import { CreditScoreDrawerProvider } from 'containers/CreditReport/CreditScoreDrawerProvider';
import { NavExpansionProvider, useNavExpansion } from 'containers/App/Header/NavExpansionContext';
import {
  navBarWidth,
  navBarWidthCollapsed,
  navBarWidthOpen,
  navToggleTransition,
} from 'containers/App/Header/commonNavStyles';
import DemoModeBar from 'containers/DemoModeBar';
import PrivacyModeBar from 'containers/PrivacyModeBar';
import Services from 'containers/Services';
import UserGuide from 'containers/UserGuide';
import { useConnectedAiLanding } from 'hooks/useConnectedAiLanding';
import FeatureErrorFallback from './FeatureErrorFallback';

const FeatureRoot = ({
  hideNavigation,
  isGetStartedRoute,
  marginRight,
  children,
}: {
  hideNavigation: boolean;
  isGetStartedRoute: boolean;
  marginRight: number;
  children: ReactNode;
}) => {
  const { collapsible, compact, expanded } = useNavExpansion();
  // Fixed-position consumers offset by the nav width even when feature-root has no margin (pre-GSF).
  const navOffset = isGetStartedRoute
    ? 0
    : hideNavigation
      ? collapsible
        ? navBarWidthCollapsed
        : navBarWidth
      : compact
        ? 0
        : expanded
          ? navBarWidthOpen
          : collapsible
            ? navBarWidthCollapsed
            : navBarWidth;

  return (
    <Box
      id='feature-root'
      data-testid='feature-root'
      ml={`${hideNavigation ? 0 : navOffset}px`}
      mr={`${marginRight}px`}
      position='relative'
      display='flex'
      flexDirection='column'
      height='100%'
      overflow='auto'
      sx={{
        '--nav-offset': `${navOffset}px`,
        '--topbar-left-offset': `${hideNavigation ? 0 : collapsible ? navOffset : navBarWidth}px`,
        // Keep page-local stacking contexts below global navigation. Root
        // modal drawers portal outside this boundary and remain above it.
        isolation: 'isolate',
        transition: (theme) =>
          [
            ...(collapsible ? [theme.transitions.create('margin-left', navToggleTransition(theme))] : []),
            theme.transitions.create('margin-right', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          ].join(', '),
      }}
    >
      {children}
    </Box>
  );
};

const LayoutDatasetEntitledContent = () => {
  const { collapsible } = useNavExpansion();
  const demoMode = isDemoMode();
  const privacyMode = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'privacyMode'));
  const [sPrivacyMode] = usePreferenceV2('sPrivacyMode');
  const isPrivacyMode = privacyMode && sPrivacyMode;
  const { viewMode, drawerWidth } = useChatDock();
  const isChatOpen = useAppSelector(aiChatSelectors.isChatOpen);
  const isGetStartedRoute = useMatch('/get-started') !== null;
  const hideNavButtons = useAppSelector(
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'gsfScreen', 'completed_gsf') !== 'completed_gsf',
  );
  const hideNavigation = isGetStartedRoute || hideNavButtons;

  useConnectedAiLanding();

  return (
    <Box id='app-router-root' height='100vh' display='flex' flexDirection='column'>
      <Collapse in={Boolean(demoMode)} style={{ minHeight: 'unset' }}>
        <DemoModeBar />
      </Collapse>

      <Collapse in={isPrivacyMode} style={{ minHeight: 'unset' }}>
        <PrivacyModeBar />
      </Collapse>

      <Box position='relative' flexGrow={1} overflow='auto'>
        {!isGetStartedRoute && <Header />}
        <FeatureRoot
          hideNavigation={hideNavigation}
          isGetStartedRoute={isGetStartedRoute}
          marginRight={viewMode === 'docked' && isChatOpen ? drawerWidth : 0}
        >
          <ErrorBoundary {...(collapsible ? { FallbackComponent: FeatureErrorFallback } : {})}>
            <Outlet />
          </ErrorBoundary>
        </FeatureRoot>

        <Services />

        <ErrorBoundary>
          <ChatLauncher />
        </ErrorBoundary>

        <ErrorBoundary>
          <UserGuide />
        </ErrorBoundary>
      </Box>
    </Box>
  );
};

const LayoutDatasetEntitled = () => {
  const isGetStartedRoute = useMatch('/get-started') !== null;
  return (
    <ChatDockProvider>
      <NavExpansionProvider navigationAvailable={!isGetStartedRoute}>
        <CreditScoreDrawerProvider>
          <LayoutDatasetEntitledContent />
        </CreditScoreDrawerProvider>
      </NavExpansionProvider>
    </ChatDockProvider>
  );
};

export default LayoutDatasetEntitled;
